import { useState, useEffect } from 'react'
import type { MediaType, ModelType, InputMode, CampaignType, GeoRegion, MarketingReport } from '../types'
import { GEO_REGIONS } from '../types'
import { generateMarketingStrategy, hasApiKey, getApiKeySource, checkUsageLimits, type ChatSession, type UsageLimits } from '../services/aiService'
import { analyticsService } from '../services/analyticsService'

interface InputFormProps {
  onReportGenerated: (report: MarketingReport, session: ChatSession, fromCache?: boolean) => void
  isLoading: boolean
  setIsLoading: (loading: boolean) => void
  onShowContact?: () => void
}

export function InputForm({ onReportGenerated, isLoading, setIsLoading, onShowContact }: InputFormProps) {
  const [mediaType, setMediaType] = useState<MediaType>('tv')
  const [modelType, setModelType] = useState<ModelType>('fast')
  const [inputMode, setInputMode] = useState<InputMode>('existing')
  const [campaignType, setCampaignType] = useState<CampaignType>('consumer')
  const [selectedRegions, setSelectedRegions] = useState<GeoRegion[]>(['USCAN'])
  const [userInput, setUserInput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [limits, setLimits] = useState<UsageLimits | null>(null)

  const toggleRegion = (region: GeoRegion) => {
    setSelectedRegions(prev =>
      prev.includes(region)
        ? prev.filter(r => r !== region)
        : [...prev, region]
    )
  }

  const apiKeyAvailable = hasApiKey()
  const apiKeySource = getApiKeySource()
  const ref = analyticsService.getRef()

  // Check usage limits on mount and when ref changes
  useEffect(() => {
    checkUsageLimits(ref).then(setLimits)
  }, [ref])

  // Ensure model selection is valid for user's tier
  useEffect(() => {
    if (limits && !limits.modelAccess.includes(modelType)) {
      setModelType('fast')
    }
  }, [limits, modelType])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!userInput.trim() || isLoading) return

    // Check limits before generating
    if (limits && !limits.allowed) {
      setError(`Daily limit reached (${limits.limit} reports). ${limits.hasRef ? 'Try again tomorrow.' : 'Use a tracked link for higher limits.'}`)
      return
    }

    // Ensure selected model is allowed
    const effectiveModel = (limits && !limits.modelAccess.includes(modelType)) ? 'fast' : modelType

    setError(null)
    setIsLoading(true)

    try {
      const { report, chatSession, fromCache } = await generateMarketingStrategy(
        userInput.trim(),
        mediaType,
        effectiveModel,
        inputMode,
        campaignType,
        selectedRegions
      )
      onReportGenerated(report, chatSession, fromCache)
      // Refresh limits after successful generation
      checkUsageLimits(ref).then(setLimits)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred')
    } finally {
      setIsLoading(false)
    }
  }

  const placeholderText = inputMode === 'existing'
    ? mediaType === 'tv'
      ? 'Enter a TV show title (e.g., "Stranger Things", "The Last of Us", "Severance")'
      : 'Enter a movie title (e.g., "Oppenheimer", "Barbie", "Dune: Part Two")'
    : mediaType === 'tv'
      ? 'Describe your TV show concept (e.g., "A psychological thriller about a therapist who discovers her patients are all connected to a mysterious cult...")'
      : 'Describe your movie concept (e.g., "A sci-fi epic about the first human colony on Mars facing an unexpected alien discovery...")'

  return (
    <div className="max-w-3xl mx-auto">
      <div className="text-center mb-12">
        <h2 className="cyber-heading text-3xl sm:text-4xl mb-4">
          Generate Marketing Strategy
        </h2>
        <p className="text-gray-400 max-w-xl mx-auto">
          Powered by Gemini, our AI strategists will craft a comprehensive
          marketing plan tailored to your {mediaType === 'tv' ? 'TV show' : 'film'}.
        </p>
      </div>

      {/* Usage Quota Banner */}
      {limits && (
        <div className={`cyber-card mb-6 ${limits.allowed ? 'border-cyber-blue/30' : 'border-red-500/30'}`}>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${limits.allowed ? 'bg-cyber-blue' : 'bg-red-500'} animate-pulse`} />
              <div>
                <span className="text-sm font-medium">
                  {limits.tier === 'admin' ? (
                    'Admin Access'
                  ) : limits.remaining === Infinity ? (
                    'Unlimited Reports'
                  ) : (
                    `${limits.remaining} of ${limits.limit} reports remaining`
                  )}
                </span>
                <span className="text-xs text-gray-500 ml-2">
                  ({limits.tier === 'anonymous' ? 'Guest' : limits.tier === 'tracked' ? `Tracked: ${ref}` : 'Admin'})
                </span>
              </div>
            </div>
            {limits.tier === 'anonymous' && (
              <div className="text-xs text-gray-400">
                <span className="text-cyber-blue">Tip:</span> Add <code className="bg-cyber-darker px-1 rounded">?ref=yourname</code> to URL for 10 reports/day + more models
              </div>
            )}
            {limits.tier === 'tracked' && !limits.modelAccess.includes('pro') && (
              <button
                type="button"
                onClick={onShowContact}
                className="text-xs text-cyber-purple hover:text-cyber-pink transition-colors"
              >
                Need Pro access? Contact us
              </button>
            )}
          </div>
        </div>
      )}

      {/* API Key Status */}
      <div className={`cyber-card mb-6 ${apiKeyAvailable ? 'border-green-500/30' : 'border-red-500/30'}`}>
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${apiKeyAvailable ? 'bg-green-500' : 'bg-red-500'} animate-pulse`} />
          <div>
            <span className="text-sm font-medium">
              API Status: {apiKeyAvailable ? 'Connected' : 'Not Connected'}
            </span>
            <span className="text-xs text-gray-500 ml-2">
              ({apiKeySource})
            </span>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="cyber-card">
        {/* Media Type Toggle */}
        <div className="mb-6">
          <label className="cyber-label">Media Type</label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setMediaType('tv')}
              className={`flex-1 py-3 px-4 rounded-lg font-display uppercase tracking-wider text-sm transition-all ${
                mediaType === 'tv'
                  ? 'bg-cyber-pink text-white shadow-neon-pink'
                  : 'bg-cyber-gray text-gray-400 hover:text-white'
              }`}
            >
              TV Show
            </button>
            <button
              type="button"
              onClick={() => setMediaType('movie')}
              className={`flex-1 py-3 px-4 rounded-lg font-display uppercase tracking-wider text-sm transition-all ${
                mediaType === 'movie'
                  ? 'bg-cyber-pink text-white shadow-neon-pink'
                  : 'bg-cyber-gray text-gray-400 hover:text-white'
              }`}
            >
              Movie
            </button>
          </div>
        </div>

        {/* Input Mode Toggle */}
        <div className="mb-6">
          <label className="cyber-label">Input Mode</label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setInputMode('existing')}
              className={`flex-1 py-3 px-4 rounded-lg font-mono text-sm transition-all ${
                inputMode === 'existing'
                  ? 'bg-cyber-blue/20 border border-cyber-blue text-cyber-blue'
                  : 'bg-cyber-gray text-gray-400 hover:text-white border border-transparent'
              }`}
            >
              <div className="font-semibold">Existing IP</div>
              <div className="text-xs opacity-70 mt-1">Research real titles</div>
            </button>
            <button
              type="button"
              onClick={() => setInputMode('concept')}
              className={`flex-1 py-3 px-4 rounded-lg font-mono text-sm transition-all ${
                inputMode === 'concept'
                  ? 'bg-cyber-blue/20 border border-cyber-blue text-cyber-blue'
                  : 'bg-cyber-gray text-gray-400 hover:text-white border border-transparent'
              }`}
            >
              <div className="font-semibold">New Concept</div>
              <div className="text-xs opacity-70 mt-1">Create from scratch</div>
            </button>
          </div>
        </div>

        {/* Campaign Type Toggle */}
        <div className="mb-6">
          <label className="cyber-label">Campaign Type</label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setCampaignType('consumer')}
              className={`flex-1 py-3 px-4 rounded-lg font-mono text-sm transition-all ${
                campaignType === 'consumer'
                  ? 'bg-green-500/20 border border-green-500 text-green-400'
                  : 'bg-cyber-gray text-gray-400 hover:text-white border border-transparent'
              }`}
            >
              <div className="font-semibold">Consumer Marketing</div>
              <div className="text-xs opacity-70 mt-1">Audience & Box Office</div>
            </button>
            <button
              type="button"
              onClick={() => setCampaignType('fyc')}
              className={`flex-1 py-3 px-4 rounded-lg font-mono text-sm transition-all ${
                campaignType === 'fyc'
                  ? 'bg-yellow-500/20 border border-yellow-500 text-yellow-400'
                  : 'bg-cyber-gray text-gray-400 hover:text-white border border-transparent'
              }`}
            >
              <div className="font-semibold">FYC Awards</div>
              <div className="text-xs opacity-70 mt-1">Academy & Guild Voters</div>
            </button>
          </div>
        </div>

        {/* Geographic Regions */}
        <div className="mb-6">
          <label className="cyber-label">Target Regions</label>
          <p className="text-xs text-gray-500 mb-3">Select markets for localized strategy recommendations</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {GEO_REGIONS.map((region) => (
              <button
                key={region.value}
                type="button"
                onClick={() => toggleRegion(region.value)}
                className={`py-2 px-3 rounded-lg text-sm transition-all ${
                  selectedRegions.includes(region.value)
                    ? 'bg-cyan-500/20 border border-cyan-500 text-cyan-400'
                    : 'bg-cyber-gray text-gray-400 hover:text-white border border-transparent'
                }`}
              >
                <div className="font-semibold">{region.label}</div>
                <div className="text-xs opacity-70 truncate">{region.description}</div>
              </button>
            ))}
          </div>
          {selectedRegions.length === 0 && (
            <p className="text-xs text-yellow-500 mt-2">Select at least one region</p>
          )}
        </div>

        {/* Model Selection */}
        <div className="mb-6">
          <label className="cyber-label">AI Model</label>
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => setModelType('fast')}
              className={`py-3 px-3 rounded-lg text-sm transition-all ${
                modelType === 'fast'
                  ? 'bg-cyber-purple/20 border border-cyber-purple text-cyber-purple'
                  : 'bg-cyber-gray text-gray-400 hover:text-white border border-transparent'
              }`}
            >
              <div className="font-semibold font-display">Fast</div>
              <div className="text-xs opacity-70 mt-1">Sonnet 4.6</div>
            </button>
            <button
              type="button"
              onClick={() => limits?.modelAccess.includes('pro') && setModelType('pro')}
              disabled={!limits?.modelAccess.includes('pro')}
              className={`py-3 px-3 rounded-lg text-sm transition-all relative ${
                !limits?.modelAccess.includes('pro')
                  ? 'bg-cyber-gray/50 text-gray-600 cursor-not-allowed border border-transparent'
                  : modelType === 'pro'
                  ? 'bg-cyber-purple/20 border border-cyber-purple text-cyber-purple'
                  : 'bg-cyber-gray text-gray-400 hover:text-white border border-transparent'
              }`}
              title={!limits?.modelAccess.includes('pro') ? 'Contact us for Pro access' : ''}
            >
              <div className="font-semibold font-display">Pro</div>
              <div className="text-xs opacity-70 mt-1">Opus 4.6</div>
              {!limits?.modelAccess.includes('pro') && (
                <div className="absolute -top-2 -right-2 bg-yellow-500 text-black text-[10px] px-1.5 py-0.5 rounded-full font-bold">
                  ADMIN
                </div>
              )}
            </button>
            <button
              type="button"
              onClick={() => limits?.modelAccess.includes('thinking') && setModelType('thinking')}
              disabled={!limits?.modelAccess.includes('thinking')}
              className={`py-3 px-3 rounded-lg text-sm transition-all relative ${
                !limits?.modelAccess.includes('thinking')
                  ? 'bg-cyber-gray/50 text-gray-600 cursor-not-allowed border border-transparent'
                  : modelType === 'thinking'
                  ? 'bg-cyber-purple/20 border border-cyber-purple text-cyber-purple'
                  : 'bg-cyber-gray text-gray-400 hover:text-white border border-transparent'
              }`}
              title={!limits?.modelAccess.includes('thinking') ? 'Use a tracked link (?ref=name) to unlock' : ''}
            >
              <div className="font-semibold font-display">Thinking</div>
              <div className="text-xs opacity-70 mt-1">Adaptive</div>
              {!limits?.modelAccess.includes('thinking') && (
                <div className="absolute -top-2 -right-2 bg-cyber-blue text-black text-[10px] px-1.5 py-0.5 rounded-full font-bold">
                  REF
                </div>
              )}
            </button>
          </div>
          {limits && limits.tier === 'anonymous' && (
            <p className="text-xs text-gray-500 mt-2">
              Add <code className="text-cyber-blue">?ref=yourname</code> to unlock Thinking mode
            </p>
          )}
        </div>

        {/* User Input */}
        <div className="mb-6">
          <label className="cyber-label">
            {inputMode === 'existing' ? 'Title' : 'Concept Description'}
          </label>
          <textarea
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            placeholder={placeholderText}
            rows={inputMode === 'existing' ? 2 : 5}
            className="cyber-input resize-none"
            disabled={isLoading}
          />
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm">
            <div className="font-semibold mb-1">Error</div>
            {error}
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={!userInput.trim() || isLoading || !apiKeyAvailable || selectedRegions.length === 0}
          className="cyber-button w-full"
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Generating Strategy...
            </span>
          ) : (
            'Generate Marketing Strategy'
          )}
        </button>

        {!apiKeyAvailable && (
          <p className="mt-4 text-center text-sm text-yellow-500">
            Please configure your Gemini API key to generate strategies.
          </p>
        )}
      </form>

      {/* Features List */}
      <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="cyber-card text-center">
          <div className="text-cyber-blue text-2xl mb-2">
            <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <div className="font-display text-sm uppercase tracking-wider">Audience Analysis</div>
          <div className="text-xs text-gray-500 mt-1">Demographics & Psychographics</div>
        </div>
        <div className="cyber-card text-center">
          <div className="text-cyber-pink text-2xl mb-2">
            <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <div className="font-display text-sm uppercase tracking-wider">Key Art Concepts</div>
          <div className="text-xs text-gray-500 mt-1">AI-Generated Visuals</div>
        </div>
        <div className="cyber-card text-center">
          <div className="text-cyber-purple text-2xl mb-2">
            <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <div className="font-display text-sm uppercase tracking-wider">Chat with Strategist</div>
          <div className="text-xs text-gray-500 mt-1">Refine Your Plan</div>
        </div>
      </div>
    </div>
  )
}
